import { memo, useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
    useCurrentRefinements,
    useDynamicWidgets,
    UseDynamicWidgetsProps,
} from 'react-instantsearch';
import { TranslationKey } from '~/lib';
import Flex from '~/shared/components/Flex';
import { Button } from '~/shared/components';
import ChevronIcon from '~/icons/chevron.svg';
import { useTranslation } from '~/shared/utils/translation';
import styles from './FilterList.module.scss';
import {
    RefinementToggle,
    RefinementListDropdown,
    RefinementAlphabeticListDropdown,
    RefinementSalesOffersDropdown,
    RefinementListColor,
    RefinementPriceDropdown,
} from './components';
import { colorSwatchKey } from '../../hooks/useColorSwatches';
import { RefinementProps } from './components/RefinementProps';
import { Facets } from '~/services/instantsearch';
import { useVisibleAttributes } from '../../hooks/useVisibleAttributes';
import { preDefinedAttributes } from '../FilterDrawer/preDefinedAttributes';

type FilterListType = UseDynamicWidgetsProps & {
    maxWidgets?: number;
    defaultShowAllWidgets?: boolean;
};

const FilterList = (props: FilterListType) => {
    const { translate } = useTranslation();
    const {
        maxWidgets = 7,
        defaultShowAllWidgets = false,
        facets = ['*'],
        maxValuesPerFacet = 1000,
        transformItems,
    } = props;

    const { items: currentRefinements } = useCurrentRefinements();
    const { attributesToRender: attributesFromAlgolia } = useDynamicWidgets({
        maxValuesPerFacet,
        facets,
        transformItems,
    });

    const candidateAttributes = useMemo(
        () => [preDefinedAttributes.newsToggles, ...attributesFromAlgolia],
        [attributesFromAlgolia],
    );

    // Hides a facet with no matching options instead of showing an empty dropdown.
    const allAttributes = useVisibleAttributes(candidateAttributes, [
        preDefinedAttributes.newsToggles,
        Facets.PRICE_RANGE,
        Facets.ACTIVE_PROMO_TYPE,
        colorSwatchKey,
    ]);

    const [showAllWidgets, setShowAllWidgets] = useState(() => {
        if (defaultShowAllWidgets) return true;

        // We don't need to show all widgets if there are no active attributes to consider.
        if (!currentRefinements.length) return false;

        // If at least one of the current refinements are not shown initially, we will show all widgets.
        const initialAttributes = allAttributes.slice(0, maxWidgets);
        const shouldShowAllWidgets = currentRefinements.some(
            (currentRefinement) => !initialAttributes.includes(currentRefinement.attribute),
        );

        return shouldShowAllWidgets;
    });

    const toggleAllWidgets = useCallback(() => setShowAllWidgets((prevState) => !prevState), []);

    const isWidgetVisible = useCallback(
        (index: number) => showAllWidgets || (!showAllWidgets && index < maxWidgets),
        [maxWidgets, showAllWidgets],
    );

    const canToggleMoreWidgets = allAttributes.length > maxWidgets;

    const buttonText = showAllWidgets ? translate('plp.fewerFilters') : translate('plp.allFilters');

    return (
        <Flex
            gap="extrasmall"
            alignItems="flex-start"
            wrap="wrap"
            className={clsx(styles.container, { [styles.showAll]: showAllWidgets })}
        >
            {allAttributes.map((attribute, index) => {
                const sharedProps: RefinementProps & { key: string } = {
                    key: attribute,
                    isVisible: isWidgetVisible(index),
                    label: translate(`plp.facet.${attribute}` as TranslationKey),
                    attribute,
                };

                switch (attribute) {
                    case colorSwatchKey:
                        return null;

                    // Rendered once already via the preDefinedAttributes.newsToggles case below
                    // (as the grouped Tilbud m.m. dropdown) — cust_activepromo.promotiontype is
                    // now a real, dynamic-widget-discoverable Algolia facet in its own right, so
                    // without this it would also fall into the default case and render a second,
                    // raw/ungrouped dropdown for the same underlying data.
                    case Facets.ACTIVE_PROMO_TYPE:
                        return null;

                    case preDefinedAttributes.newsToggles:
                        return <RefinementSalesOffersDropdown {...sharedProps} />;

                    case Facets.SOLE_REPRESENTATION:
                    case Facets.IS_NEW:
                    case Facets.ALLERGY_FRIENDLY:
                    case Facets.PERSONALIZATION:
                        return <RefinementToggle {...sharedProps} />;

                    case Facets.PRICE_RANGE:
                        return <RefinementPriceDropdown {...sharedProps} />;

                    case Facets.BRAND:
                    case Facets.SERIES:
                    case Facets.DESIGNER:
                        return (
                            <RefinementAlphabeticListDropdown
                                {...sharedProps}
                                searchable
                                searchPlaceholder={translate(
                                    `plp.facet.search.${attribute}` as TranslationKey,
                                )}
                            />
                        );

                    case Facets.COLOR:
                        return (
                            <RefinementListColor
                                {...sharedProps}
                                searchable
                                placeholder={translate(
                                    `plp.facet.search.${attribute}` as TranslationKey,
                                )}
                            />
                        );

                    default:
                        return (
                            <RefinementListDropdown
                                {...sharedProps}
                                searchable
                                searchPlaceholder={translate(
                                    `plp.facet.search.${attribute}` as TranslationKey,
                                )}
                            />
                        );
                }
            })}

            {canToggleMoreWidgets && (
                <Button
                    className={styles.filterToggleButton}
                    aria-label={buttonText}
                    variant="inverse-plp"
                    small
                    onClick={toggleAllWidgets}
                >
                    <Flex gap="extrasmall" justifyContent="center" alignItems="center">
                        <div style={{ whiteSpace: 'nowrap' }}>{buttonText}</div>
                        <ChevronIcon
                            className={clsx(styles.filterIcon, {
                                [styles.isOpen]: showAllWidgets,
                            })}
                        />
                    </Flex>
                </Button>
            )}
        </Flex>
    );
};

export default memo(FilterList);
